import React from 'react';
import { SendIcon } from 'lucide-react';
import { usePortalQuery, usePortalMutation } from '../../hooks/usePortalQuery';
import { listMessages, markConversationRead, sendMessage } from '../../lib/portalApi';
import { usePortalAuth } from '../../context/PortalAuth';
import { Avatar } from './Avatar';
import { InlineError, QueryState } from './QueryState';
import { displayName, formatDateTime } from '../../utils/portalFormat';
interface MessageThreadProps {
  orgId: string;
  conversationId: string;
  canSend: boolean;
}
async function sendAndConfirm(orgId: string, conversationId: string, body: string): Promise<true> {
  await sendMessage(orgId, conversationId, body);
  return true;
}

/** A conversation with Cavalla, oldest first, marked as read when opened. */
export function MessageThread({
  orgId,
  conversationId,
  canSend
}: MessageThreadProps) {
  const {
    user
  } = usePortalAuth();
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => listMessages(orgId, conversationId), [orgId, conversationId]);
  const send = usePortalMutation(sendAndConfirm);
  const [body, setBody] = React.useState('');
  const endRef = React.useRef<HTMLDivElement>(null);
  const count = data?.data.length ?? 0;
  React.useEffect(() => {
    void markConversationRead(orgId, conversationId).catch(() => undefined);
  }, [orgId, conversationId, count]);
  React.useEffect(() => {
    endRef.current?.scrollIntoView({
      block: 'nearest'
    });
  }, [count]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!body.trim() || send.pending) return;
    // True only on success, so a failed send keeps the draft in the box.
    const sent = await send.run(orgId, conversationId, body);
    if (sent) setBody('');
  };
  return <div className="flex flex-col" data-id="element-976" data-mp="1109pozqn7a4k">
      <QueryState loading={loading} error={error} data-id="element-977" data-mp="10x3z1g2d806q">
        <ol className="space-y-5" aria-label="Messages" data-id="element-978" data-mp="27eirlz1py2mz">
          {data?.data.map(message => {
          const mine = message.senderId === user?.id;
          return <li key={message.id} className={`flex gap-3 ${mine ? 'flex-row-reverse' : ''}`} data-id="element-979" data-mp="cbuln08t8ynm">
                <Avatar user={message.sender} size="sm" data-id="element-980" data-mp="8bbuz9inlyn0" />
                <div className={`max-w-[85%] sm:max-w-[70%] ${mine ? 'text-right' : ''}`} data-id="element-981" data-mp="8cva2wskq3n7">
                  <p className="text-[12px] text-muted" data-id="element-982" data-mp="m9s93liuirf6">
                    <span className="text-body" data-id="element-983" data-mp="hb7b7sr2gjtw">{mine ? 'You' : displayName(message.sender)}</span>
                    {message.sender.isInternal && !mine && <span className="text-gold" data-id="element-984" data-mp="3prjxkpxrwt8m">, Cavalla</span>}
                    {' · '}
                    <time dateTime={message.createdAt} data-id="element-985" data-mp="3ku0l9vkmyqjb">{formatDateTime(message.createdAt)}</time>
                  </p>
                  <p className={`mt-1 inline-block whitespace-pre-wrap rounded-md px-4 py-2.5 text-left text-[15px] leading-relaxed ${mine ? 'bg-line text-ink' : 'border border-line bg-panel text-ink'}`} data-id="element-986" data-mp="3dsdm0qnt5qu">
                    {message.body}
                  </p>
                </div>
              </li>;
        })}
        </ol>
        <div ref={endRef} data-id="element-987" data-mp="1hj5ccueqnbi1" />
      </QueryState>

      {canSend ? <form onSubmit={submit} className="mt-6 border-t border-line pt-5" data-id="element-988" data-mp="1hhu8kulam3qx">
          <label htmlFor={`reply-${conversationId}`} className="sr-only" data-id="element-989" data-mp="1mborhvt2qeoy">
            Write a message
          </label>
          <textarea id={`reply-${conversationId}`} rows={3} value={body} maxLength={5000} onChange={e => setBody(e.target.value)} onKeyDown={e => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void submit(e);
      }} placeholder="Write to the team" className="w-full resize-y rounded-sm border border-line bg-charcoal px-4 py-3 text-[15px] text-ink placeholder:text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-990" data-mp="18h5vq37my9fm" />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-id="element-991" data-mp="ft08n9t408cm">
            <p className="text-[12px] text-muted" data-id="element-992" data-mp="ku62jayia1nq">The team usually replies within one working day.</p>
            <button type="submit" disabled={!body.trim() || send.pending} className="inline-flex h-10 items-center gap-2 rounded-sm bg-gold px-4 text-[14px] font-medium text-charcoal transition-colors duration-150 hover:bg-[#ff7440] disabled:opacity-50" data-id="element-993" data-mp="103t9brm5u2nz">
              <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-994" data-mp="mdhhzuxeg7xw" />
              Send
            </button>
          </div>
          <div className="mt-2" data-id="element-995" data-mp="1izjfzs51fn87">
            <InlineError error={send.error} data-id="element-996" data-mp="1e2axtrjw3j6y" />
          </div>
        </form> : <p className="mt-6 border-t border-line pt-5 text-[14px] text-muted" data-id="element-997" data-mp="1a0z3x9tzgbsy">Your role is read only, so you can follow this conversation but not reply.</p>}
    </div>;
}